// src/components/StudentDashboard.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import XPBar from "./XPBar";
import { isStudentView, exitStudentView } from "../utils/studentView";
import DevStudentResetPanel from "./DevStudentResetPanel";
import LessonsPane from "./LessonsPane";
import StudentAccount from "./StudentAccount";
import StudentNotifications from "./StudentNotifications";
import TutorCurriculumExplorer from "./TutorCurriculumExplorer";
import { canonicalLessonId, studentLessonPathFor } from "../utils/lessonRoute";
import { canAccessTutorTools, getAuthUserHeader, getUserRole, isAdminUser, readStoredAuthUser } from "../utils/userRoles";
import "./StudentPortal.css";

import { API_BASE_URL } from "../config/apiBase";
import { downloadStudentCertificate } from "../utils/downloadStudentCertificate";

const API = API_BASE_URL;
const TUTOR_NOTE_NOTIFICATION_PREFIX = "Shaanzae left guidance";

function parseTutorGuidanceLessonId(message) {
  const match = String(message || "").match(/Week\s+(\d+)\s*,\s*Lesson\s+(\d+)/i);
  if (!match) return null;
  return `W${match[1]}L${match[2]}`;
}

function isTutorGuidanceNotification(message) {
  return String(message || "")
    .trim()
    .toLowerCase()
    .startsWith(TUTOR_NOTE_NOTIFICATION_PREFIX.toLowerCase());
}

function noteLessonId(note) {
  if (!note) return null;
  if (note.lesson_id) return String(note.lesson_id).toUpperCase();
  if (
    note.week === null ||
    note.week === undefined ||
    note.week === "" ||
    note.lesson === null ||
    note.lesson === undefined ||
    note.lesson === ""
  ) {
    return null;
  }
  return `W${Number(note.week)}L${Number(note.lesson)}`;
}

const AVATAR_SOLID_COLOR_KEYS = new Set(["muted", "sand", "ivory"]);
const AVATAR_HUE_BY_KEY = {
  red: "330deg",
  yellow: "30deg",
  turquoise: "160deg",
  blue: "210deg",
};

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatTokenCompact(tokens) {
  const t = safeNumber(tokens, 0);
  if (t < 1000) return String(Math.round(t));
  const compact = (Math.round((t / 1000) * 10) / 10).toFixed(1);
  return `${compact.replace(/\.0$/, "")}k`;
}

function formatCost(value) {
  const n = safeNumber(value, 0);
  return `$${n.toFixed(4)}`;
}

function formatUsageSourceLabel(source) {
  const value = String(source || "").trim().toLowerCase();
  if (value === "actual") return "confirmed";
  if (value === "estimated") return "estimated";
  return "unavailable";
}

function parseConfigBool(value) {
  if (value === true) return true;
  if (value === false || value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "on";
}

function clearStudentDraftCache(studentId) {
  const sid = String(studentId || "").trim();
  if (!sid) return;
  const upper = sid.toUpperCase();
  const prefixes = [`draft:${upper}:`, `draft:${sid}:`];
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }
}

function clearStudentRuntimeCache(studentId) {
  const sid = String(studentId || "").trim();
  if (!sid) return;
  const upper = sid.toUpperCase();
  const keyPrefixes = [
    `draft:${upper}:`,
    `draft:${sid}:`,
    `structured_draft:${upper}:`,
    `structured_draft:${sid}:`,
    `pending_submission:${upper}:`,
    `pending_submission:${sid}:`,
    `writeflow_focus_mode_${upper}`,
    `writeflow_focus_mode_${sid}`,
    `writeflow_demo_onboarding_completed_${upper}`,
    `writeflow_demo_onboarding_completed_${sid}`,
  ];
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (keyPrefixes.some((prefix) => key === prefix || key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }
  try {
    const sessionKeys = Object.keys(sessionStorage);
    for (const key of sessionKeys) {
      if (key === `writeflow_focus_mode_${upper}` || key === `writeflow_focus_mode_${sid}`) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if sessionStorage is unavailable
  }
}

function SidebarToggleIcon({ open }) {
  return (
    <svg
      className={`sidebar-toggle-svg ${open ? "is-open" : "is-closed"}`}
      width="28"
      height="28"
      viewBox="0 0 28 28"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="6" y1="8" x2="22" y2="8" />
      <line x1="6" y1="14" x2="22" y2="14" />
      <line x1="6" y1="20" x2="22" y2="20" />
    </svg>
  );
}

export default function StudentDashboard() {
  // Route is /student/dashboard/:id
  const { id, studentId: routeStudentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const routeTargetStudentId = (id || routeStudentId || "").toUpperCase();
  const storedAuthUser = readStoredAuthUser() || {};
  const authenticatedStudentId = String(storedAuthUser.student_id || storedAuthUser.username || localStorage.getItem("student_id") || "").toUpperCase();
  const studentId = routeTargetStudentId || authenticatedStudentId;
  const previewRequested = new URLSearchParams(location.search).get("preview") === "admin";
  const authenticatedRole = getUserRole(storedAuthUser, localStorage.getItem("role"));
  const adminPreview = previewRequested && (authenticatedRole === "admin" || authenticatedRole === "tutor") && authenticatedStudentId !== studentId;
  // A student route does not change the signed-in identity. Only the explicit
  // read-only Student View may switch persona; legacy tutor preview stays explicit.
  useEffect(() => {
    if (isStudentView() || !authenticatedStudentId || studentId === authenticatedStudentId || adminPreview) return;
    if (authenticatedRole === "admin") {
      navigate(`/admin/users/${encodeURIComponent(studentId)}`, { replace: true });
    } else if (authenticatedRole === "tutor") {
      navigate("/tutor/student-progress", { replace: true });
    }
  }, [adminPreview, authenticatedRole, authenticatedStudentId, studentId, navigate]);
  const previewWeek = location.pathname.match(/\/week-mentor\/(\d+)/)?.[1] || "";
  const [authenticatedName, setAuthenticatedName] = useState("");
  const accountName = String(authenticatedName || storedAuthUser.name || (authenticatedStudentId === studentId ? localStorage.getItem("student_name") : "") || authenticatedStudentId).trim() || authenticatedStudentId;
  useEffect(() => {
    let cancelled = false;
    setAuthenticatedName("");
    if (isStudentView() || !authenticatedStudentId) return undefined;
    // Recover the real account name even if an earlier page polluted the cache.
    fetch(`${API}/api/auth/me`, { headers: { "X-User": getAuthUserHeader() }, cache: "no-store" })
      .then(async (response) => response.ok ? response.json() : null)
      .then((payload) => {
        const user = payload?.user;
        const id = String(user?.student_id || user?.username || "").toUpperCase();
        if (!cancelled && id === authenticatedStudentId && user?.name) {
          setAuthenticatedName(user.name);
        }
      }).catch(() => {});
    return () => { cancelled = true; };
  }, [authenticatedStudentId]);
  const [studentName, setStudentName] = useState(() => localStorage.getItem("student_name") || studentId);
  const currentRole = getUserRole(storedAuthUser.student_id ? storedAuthUser : authenticatedStudentId, localStorage.getItem("role"));
  const isAdmin = isAdminUser({
    student_id: authenticatedStudentId,
    role: authenticatedRole,
    is_admin: localStorage.getItem("is_admin") === "1",
  });
  const displayRoleName =
    currentRole ? currentRole.charAt(0).toUpperCase() + currentRole.slice(1) : "Student";
  const displayStudentName = String(accountName || studentName || authenticatedStudentId || studentId)
    .replace(/^student\s+/i, "")
    .trim() || studentId;
  const avatarLabel =
    String(displayStudentName || "")
      .replace(/\s+/g, "")
      .slice(0, 2)
      .toUpperCase() || "SM";

  const [iconColor, setIconColor] = useState(() => {
    if (!studentId) return "default";
    return localStorage.getItem(`student_icon_color_${studentId}`) || "default";
  });

  const [notifOpen, setNotifOpen] = useState(false);
  const notifWrapRef = useRef(null);
  const panelReturnPathRef = useRef(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [tutorNotes, setTutorNotes] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lessonsPanelOpen, setLessonsPanelOpen] = useState(true);
  const [panelClosing, setPanelClosing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [certificateReady, setCertificateReady] = useState(false);
  const [certificateDownloadError, setCertificateDownloadError] = useState("");
  const [currentLessonPath, setCurrentLessonPath] = useState(
    studentLessonPathFor(studentId, 1, 1)
  );
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const path = location.pathname;
    return !(path.includes("/lessons") || (path.includes("/week/") && path.includes("/lesson/")));
  });
  const [focusModeActive, setFocusModeActive] = useState(false);
  const [resettingProgress, setResettingProgress] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [devResetToolsEnabled, setDevResetToolsEnabled] = useState(false);
  const [devAllowTutorDashboardAccess, setDevAllowTutorDashboardAccess] = useState(false);
  const [showLlmUsage, setShowLlmUsage] = useState(false);
  const showLlmUsagePanel = isAdmin && showLlmUsage;
  const [llmUsage, setLlmUsage] = useState({
    latest_call_requests: 0,
    latest_call_tokens: 0,
    latest_call_cost: 0,
    latest_call_estimated_tokens: 0,
    latest_call_estimated_cost: 0,
    latest_call_usage_source: "unavailable",
    latest_call_usage_note: "usage unavailable",
    last_function: null,
    session_total_requests: 0,
    session_total_tokens: 0,
    session_total_cost: 0,
    avg_cost: 0,
  });
  const [llmUsageError, setLlmUsageError] = useState(false);
  const [llmUsageOpen, setLlmUsageOpen] = useState(false);
  const llmUsageWrapRef = useRef(null);
  const panelCloseTimerRef = useRef(null);

  const formatNotifTime = (value) => {
    if (!value) return "";
    const raw = String(value);
    // SQLite CURRENT_TIMESTAMP is typically "YYYY-MM-DD HH:MM:SS" (not reliably parsed by all browsers)
    const normalized = raw.includes(" ") && !raw.includes("T") ? `${raw.replace(" ", "T")}Z` : raw;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
  };
  const tutorNoteLookup = useMemo(() => {
    const map = new Map();
    for (const note of tutorNotes) {
      const key = noteLessonId(note);
      if (key && !map.has(key)) {
        map.set(key, note);
      }
    }
    return map;
  }, [tutorNotes]);

  const refreshUnreadCount = useCallback(async (signal) => {
    if (!studentId) return;
    try {
      const res = await fetch(`${API}/api/notifications/${studentId}?limit=1`, {
        cache: "no-store",
        signal,
      });
      const json = await res.json();
      const n = Number(json?.unread_count);
      if (!Number.isNaN(n)) setUnreadCount(n);
    } catch (e) {
      // ignore
    }
  }, [studentId]);

  const refreshCurrentLessonPath = useCallback(async (signal) => {
    if (!studentId) return;
    try {
      const res = await fetch(`${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}`, {
        cache: "no-store",
        signal,
      });
      const json = await res.json().catch(() => null);
      const week = Number(json?.week) || 1;
      const lesson = Number(json?.lesson) || 1;
      setDashboardData(json || null);
      setCurrentLessonPath(studentLessonPathFor(studentId, week, lesson));
    } catch {
      // Keep the local fallback.
    }
  }, [studentId]);

  const refreshCertificateReady = useCallback(async (isCurrent = () => true) => {
    if (!studentId) return;
    try {
      const response = await fetch(`${API}/api/student/course-end?student_id=${encodeURIComponent(studentId)}`, {
        headers: { "X-User": getAuthUserHeader() },
        cache: "no-store",
      });
      const payload = await response.json().catch(() => null);
      if (response.ok && isCurrent()) {
        setCertificateReady(Boolean(payload?.video_completed));
      }
    } catch {
      // Keep the last known completion state when offline.
    }
  }, [studentId]);

  const onDownloadCertificate = async () => {
    setCertificateDownloadError("");
    try {
      await downloadStudentCertificate(studentId);
    } catch (error) {
      setCertificateDownloadError(error.message || "Could not download the certificate.");
    }
  };

  const refreshLlmUsage = useCallback(async (signal) => {
    try {
      const res = await fetch(`${API}/api/llm-usage`, {
        cache: "no-store",
        signal,
      });
      if (!res.ok) throw new Error(`llm usage fetch failed (${res.status})`);
      const json = await res.json().catch(() => null);
      setLlmUsage({
        latest_call_requests: safeNumber(
          json?.latest_call_requests,
          safeNumber(json?.requests, 0) > 0 ? 1 : 0
        ),
        latest_call_tokens: safeNumber(
          json?.latest_call_tokens,
          safeNumber(json?.tokens, 0)
        ),
        latest_call_cost: safeNumber(
          json?.latest_call_cost,
          safeNumber(json?.cost, 0)
        ),
        latest_call_estimated_tokens: safeNumber(json?.latest_call_estimated_tokens, 0),
        latest_call_estimated_cost: safeNumber(json?.latest_call_estimated_cost, 0),
        latest_call_usage_source:
          typeof json?.latest_call_usage_source === "string"
            ? json.latest_call_usage_source
            : "unavailable",
        latest_call_usage_note:
          typeof json?.latest_call_usage_note === "string"
            ? json.latest_call_usage_note
            : "usage unavailable",
        last_function:
          typeof json?.last_function === "string" && json.last_function.trim()
            ? json.last_function.trim()
            : typeof json?.last_feature === "string" && json.last_feature.trim()
            ? json.last_feature.trim()
            : null,
        session_total_requests: safeNumber(
          json?.session_total_requests,
          safeNumber(json?.requests, 0)
        ),
        session_total_tokens: safeNumber(
          json?.session_total_tokens,
          safeNumber(json?.tokens, 0)
        ),
        session_total_cost: safeNumber(
          json?.session_total_cost,
          safeNumber(json?.cost, 0)
        ),
        avg_cost: safeNumber(json?.avg_cost, 0),
      });
      setLlmUsageError(false);
    } catch (e) {
      if (e?.name !== "AbortError") setLlmUsageError(true);
    }
  }, []);

  const resetLlmUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/llm-usage/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json().catch(() => null);
      setLlmUsage({
        latest_call_requests: safeNumber(json?.latest_call_requests, 0),
        latest_call_tokens: safeNumber(json?.latest_call_tokens, 0),
        latest_call_cost: safeNumber(json?.latest_call_cost, 0),
        latest_call_estimated_tokens: safeNumber(json?.latest_call_estimated_tokens, 0),
        latest_call_estimated_cost: safeNumber(json?.latest_call_estimated_cost, 0),
        latest_call_usage_source:
          typeof json?.latest_call_usage_source === "string"
            ? json.latest_call_usage_source
            : "unavailable",
        latest_call_usage_note:
          typeof json?.latest_call_usage_note === "string"
            ? json.latest_call_usage_note
            : "usage unavailable",
        last_function:
          typeof json?.last_function === "string" && json.last_function.trim()
            ? json.last_function.trim()
            : null,
        session_total_requests: safeNumber(
          json?.session_total_requests,
          safeNumber(json?.requests, 0)
        ),
        session_total_tokens: safeNumber(
          json?.session_total_tokens,
          safeNumber(json?.tokens, 0)
        ),
        session_total_cost: safeNumber(
          json?.session_total_cost,
          safeNumber(json?.cost, 0)
        ),
        avg_cost: safeNumber(json?.avg_cost, 0),
      });
      setLlmUsageError(false);
    } catch {
      setLlmUsage({
        latest_call_requests: 0,
        latest_call_tokens: 0,
        latest_call_cost: 0,
        latest_call_estimated_tokens: 0,
        latest_call_estimated_cost: 0,
        latest_call_usage_source: "unavailable",
        latest_call_usage_note: "usage unavailable",
        last_function: null,
        session_total_requests: 0,
        session_total_tokens: 0,
        session_total_cost: 0,
        avg_cost: 0,
      });
      setLlmUsageError(false);
    }
  }, []);

  const lessonsBase = `/student/dashboard/${studentId}/lessons`;
  const dashboardBase = `/student/dashboard/${studentId}`;
  const sidebarPanelRoutes = useMemo(() => [
    `${dashboardBase}/dashboard_v2`,
    `${dashboardBase}/notifications`,
    `${dashboardBase}/tutor-notes`,
    `${dashboardBase}/badges`,
    `${dashboardBase}/account`,
    lessonsBase,
  ], [dashboardBase, lessonsBase]);
  const onLessonsRoute = location.pathname.startsWith(lessonsBase);
  const currentRoute = `${location.pathname}${location.search || ""}${location.hash || ""}`;
  const showStudentProgressLink = currentRole !== "tutor";
  const isSidebarPanelRoute = useCallback((path = location.pathname) =>
    sidebarPanelRoutes.some((route) => path === route || path.startsWith(`${route}/`)),
    [location.pathname, sidebarPanelRoutes]
  );
  const isFocusedLessonRoute = useCallback((path = location.pathname) =>
    path.includes("/week/") && path.includes("/lesson/") && !path.startsWith(lessonsBase),
    [lessonsBase, location.pathname]
  );

  const rememberPanelReturnPath = () => {
    if (!isSidebarPanelRoute()) {
      panelReturnPathRef.current = currentRoute;
    }
  };

  const closeSidebarPanel = (targetPathOverride = null) => {
    if (panelCloseTimerRef.current) {
      window.clearTimeout(panelCloseTimerRef.current);
    }
    const rememberedPath = panelReturnPathRef.current;
    const targetPath =
      targetPathOverride ||
      (rememberedPath && isFocusedLessonRoute(rememberedPath) ? rememberedPath : currentLessonPath);

    setPanelClosing(true);
    setLessonsPanelOpen(false);
    panelCloseTimerRef.current = window.setTimeout(() => {
      setPanelClosing(false);
      navigate(targetPath || studentLessonPathFor(studentId, 1, 1));
      panelCloseTimerRef.current = null;
    }, 220);
  };

  const goToDashboardV2 = () => {
    rememberPanelReturnPath();
    navigate(`${dashboardBase}/dashboard_v2`);
    closeMobileDrawer();
  };

  const openLessonsPanel = () => {
    if (panelCloseTimerRef.current) {
      window.clearTimeout(panelCloseTimerRef.current);
      panelCloseTimerRef.current = null;
    }
    setPanelClosing(false);
    setLessonsPanelOpen(true);
    if (!onLessonsRoute) {
      rememberPanelReturnPath();
      navigate(lessonsBase);
    }
  };

  const goToLessons = () => {
    openLessonsPanel();
    closeMobileDrawer();
  };

  const closeMobileDrawer = () => {
    if (window.innerWidth <= 860) {
      setSidebarOpen(false);
    }
  };

  const closeLessonsPanel = (targetPathOverride = null) => {
    closeSidebarPanel(targetPathOverride);
  };

  const goToFirstLesson = () => {
    // For now: jump to Week 1, Lesson 1 (no “next-lesson” logic yet)
    navigate(studentLessonPathFor(studentId, 1, 1));
  };

  const goToAccount = () => {
    rememberPanelReturnPath();
    navigate(`${dashboardBase}/account`);
    closeMobileDrawer();
  };

  const goToBadges = () => {
    rememberPanelReturnPath();
    navigate(`${dashboardBase}/badges`);
    closeMobileDrawer();
  };

  const goToNotifications = () => {
    rememberPanelReturnPath();
    navigate(`${dashboardBase}/notifications`);
    closeMobileDrawer();
  };

  const goToTutorNotes = () => {
    rememberPanelReturnPath();
    navigate(`${dashboardBase}/tutor-notes`);
    closeMobileDrawer();
  };

  const goToGuidedDemo = () => {
    const returnTo = `/student/dashboard/${studentId}`;
    const query = new URLSearchParams({
      source: "dashboard",
      studentId,
      returnTo,
    });
    navigate(`/student/dashboard/${studentId}/guided-demo?${query.toString()}`);
  };

  const handleLogout = async () => {
    if (isStudentView()) {
      const returnTo = await exitStudentView();
      navigate(returnTo, { replace: true });
      return;
    }
    localStorage.removeItem("student_id");
    localStorage.removeItem("student_name");
    localStorage.removeItem("role");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("payment_made");
    navigate("/");
  };

  const goToLessonPosition = (week, lesson, options = {}) => {
    const w = Number(week) || 1;
    const l = Number(lesson) || 1;
    const query = options?.devJump
      ? `?dev_jump=1&jump_ts=${Date.now()}`
      : "";
    navigate(`${studentLessonPathFor(studentId, w, l)}${query}`);
  };

  const handleResetProgress = async () => {
    if (!studentId || resettingProgress) return;
    const confirmed = window.confirm(
      `Reset ALL COURSE PROGRESS for ${String(studentName || "").trim() || "the student"} (Target ID ${String(studentId || "").trim()})? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setResettingProgress(true);
      const res = await fetch(
        `${API}/api/dev/student-reset/full`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ student_id: studentId }),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error((data && data.detail) || "Failed to reset progress.");
      }
      try {
        const sid = String(studentId || "").toUpperCase();
        if (sid) {
          clearStudentRuntimeCache(sid);
          clearStudentDraftCache(sid);
          try {
            window.dispatchEvent(new CustomEvent("studentXpUpdated", { detail: { studentId: sid } }));
          } catch {
            // non-blocking
          }
          try {
            window.dispatchEvent(new CustomEvent("writeflow:lesson-reset", { detail: { studentId: sid } }));
          } catch {
            // non-blocking
          }
        }
      } catch {
        // non-blocking if localStorage is unavailable
      }
      try {
        window.dispatchEvent(
          new CustomEvent("studentProgressRefetch", {
            detail: {
              studentId,
              currentWeek: Number(data?.state?.current_week) || 1,
              currentLesson: Number(data?.state?.current_lesson) || 1,
            },
          })
        );
      } catch {
        // non-blocking
      }
      navigate(studentLessonPathFor(studentId, 1, 1), { replace: true });
    } catch (e) {
      window.alert("Could not reset progress. Please try again.");
    } finally {
      setResettingProgress(false);
    }
  };

  const todayLabel = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const onDashboardHome =
    location.pathname === `/student/dashboard/${studentId}` ||
    location.pathname === `/student/dashboard/${studentId}/`;
  const onDashboardV2 = location.pathname.includes("/dashboard_v2");
  const onAccount = location.pathname.includes("/account");
  const onNotifications = location.pathname.includes("/notifications");
  const onTutorNotes = location.pathname.includes("/tutor-notes");
  const onBadges = location.pathname.includes("/badges");
  const onGuidedDemo = location.pathname.includes("/guided-demo");
  const onLesson =
    onLessonsRoute ||
    (location.pathname.includes("/week/") && location.pathname.includes("/lesson/"));
  const focusChromeHidden = focusModeActive && onLesson;
  const weekStartMatch = location.pathname.match(/\/student\/[^/]+\/week\/(\d+)\/start(?:\/|$)/);
  const routeWeekStart = weekStartMatch ? Number(weekStartMatch[1]) : null;
  const shellWeek = Number.isInteger(routeWeekStart) ? routeWeekStart : (Number(dashboardData?.week) || 1);
  const shellLesson = Number.isInteger(routeWeekStart) ? 1 : (Number(dashboardData?.lesson) || 1);

  useEffect(() => {
    if (isFocusedLessonRoute()) {
      setCurrentLessonPath(currentRoute);
    }
    if (!isSidebarPanelRoute()) {
      panelReturnPathRef.current = currentRoute;
    }
  }, [currentRoute, isFocusedLessonRoute, isSidebarPanelRoute]);

  useEffect(() => {
    if (!studentId) return undefined;
    refreshCurrentLessonPath();
    // Ignore stale replies during unmount or Strict Mode effect replay.
    // Avoid aborting this small GET: fetch-intercepting extensions may leak its rejection.
    let active = true;
    refreshCertificateReady(() => active);
    const onProgressRefetch = (event) => {
      const sid = String(event?.detail?.studentId || "").toUpperCase();
      if (sid && sid !== studentId) return;
      refreshCurrentLessonPath();
    };
    const onVideoCompleted = (event) => {
      const sid = String(event?.detail?.studentId || "").toUpperCase();
      if (sid && sid !== studentId) return;
      refreshCertificateReady(() => active);
    };
    window.addEventListener("studentProgressRefetch", onProgressRefetch);
    window.addEventListener("studentCourseVideoCompleted", onVideoCompleted);
    return () => {
      active = false;
      window.removeEventListener("studentProgressRefetch", onProgressRefetch);
      window.removeEventListener("studentCourseVideoCompleted", onVideoCompleted);
    };
  }, [studentId, refreshCurrentLessonPath, refreshCertificateReady]);

  useEffect(() => {
    return () => {
      if (panelCloseTimerRef.current) window.clearTimeout(panelCloseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!onLesson && focusModeActive) setFocusModeActive(false);
  }, [onLesson, focusModeActive]);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Topbar search replaced by XP bar

  // Load persisted icon color from backend (and keep in sync across tabs)
  useEffect(() => {
    let cancelled = false;

    async function loadIconColor() {
      if (!studentId) return;
      try {
        const res = await fetch(`${API}/api/student_account/${studentId}`, { cache: "no-store" });
        const json = await res.json();
        const color = json?.student?.icon_color || "default";
        const name = String(json?.student?.name || "").trim();
        if (!cancelled) {
          setIconColor(color);
          localStorage.setItem(`student_icon_color_${studentId}`, color);
          if (name) {
            setStudentName(name);
            if (!isStudentView() && studentId === authenticatedStudentId) {
              localStorage.setItem("student_name", name);
            }
          }
        }
      } catch (e) {
        // Non-blocking; keep localStorage value
      }
    }

    loadIconColor();
    return () => {
      cancelled = true;
    };
  }, [studentId, authenticatedStudentId]);

  useEffect(() => {
    function onIconColorUpdated(e) {
      const sid = (e?.detail?.studentId || "").toUpperCase();
      const color = e?.detail?.iconColor;
      if (!sid || sid !== studentId || !color) return;
      setIconColor(color);
      localStorage.setItem(`student_icon_color_${studentId}`, color);
    }

    window.addEventListener("studentIconColorUpdated", onIconColorUpdated);
    return () => window.removeEventListener("studentIconColorUpdated", onIconColorUpdated);
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    async function loadConfig() {
      try {
        const res = await fetch(`${API}/api/config`, {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        });
        const json = await res.json().catch(() => null);
        if (!cancelled) {
          const devToolsFlag = parseConfigBool(json?.enable_dev_reset_tools);
          setDevResetToolsEnabled(devToolsFlag);
          setDevAllowTutorDashboardAccess(parseConfigBool(json?.dev_allow_tutor_dashboard_access));
          setShowLlmUsage(devToolsFlag);
        }
      } catch {
        if (!cancelled) {
          setDevResetToolsEnabled(false);
          setDevAllowTutorDashboardAccess(false);
          setShowLlmUsage(false);
        }
      }
    }
    loadConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showLlmUsagePanel) return undefined;

    refreshLlmUsage();

    const interval = window.setInterval(() => {
      // Dev/admin-only usage panel: slow refresh to avoid noisy polling.
      refreshLlmUsage();
    }, 60000);

    function onVisibility() {
      if (document.visibilityState === "visible") refreshLlmUsage();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [showLlmUsagePanel, refreshLlmUsage]);

  // Close notifications dropdown on outside click / escape
  useEffect(() => {
    if (!notifOpen) return;

    function onDocMouseDown(e) {
      const el = notifWrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) setNotifOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setNotifOpen(false);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [notifOpen]);

  useEffect(() => {
    if (!llmUsageOpen) return;

    function onDocMouseDown(e) {
      const el = llmUsageWrapRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) setLlmUsageOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setLlmUsageOpen(false);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [llmUsageOpen]);

  // Fetch notifications when the dropdown opens
  useEffect(() => {
    if (!notifOpen) return;
    if (!studentId) return;

    let cancelled = false;

    async function loadNotifications() {
      setNotifLoading(true);
      try {
        const res = await fetch(`${API}/api/notifications/${studentId}?limit=20`, {
          cache: "no-store",
        });
        const json = await res.json();
        if (cancelled) return;
        const list = Array.isArray(json?.notifications) ? json.notifications : [];
        const count = Number(json?.unread_count);
        if (!Number.isNaN(count)) setUnreadCount(count);
        setNotifications(list);

        const hasTutorNoteNotification = list.some((n) => isTutorGuidanceNotification(n?.message));
        if (hasTutorNoteNotification) {
          const notesRes = await fetch(`${API}/api/student/${studentId}/tutor-notes`, {
            cache: "no-store",
          });
          const notesJson = await notesRes.json();
          const notes = Array.isArray(notesJson?.notes) ? notesJson.notes : [];
          if (!cancelled) setTutorNotes(notes);
        } else if (!cancelled) {
          setTutorNotes([]);
        }

        // Mark all as read once viewed
        try {
          const r = await fetch(`${API}/api/notifications/${studentId}/read`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          });
          if (r.ok) {
            setUnreadCount(0);
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
          }
        } catch (e) {
          // ignore
        }
      } catch (e) {
        if (!cancelled && e?.name !== "AbortError") {
          setNotifications([]);
          setTutorNotes([]);
        }
      } finally {
        if (!cancelled) setNotifLoading(false);
      }
    }

    loadNotifications();
    return () => {
      cancelled = true;
    };
  }, [notifOpen, studentId]);

  // Poll unread count so the badge updates when new notifications arrive
  useEffect(() => {
    if (!studentId) return;
    if (notifOpen) return;

    refreshUnreadCount();

    const interval = window.setInterval(() => {
      // Notifications badge only: slow poll for unread count updates.
      refreshUnreadCount();
    }, 60000);

    function onVisibility() {
      if (document.visibilityState === "visible") refreshUnreadCount();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [studentId, notifOpen, refreshUnreadCount]);

  // Keep hooks unconditional; render fallback after hooks
  if (!studentId) {
    return (
      <div style={{ padding: "2rem" }}>
        <h1>Student Dashboard</h1>
        <p>No student ID found. Please go back to the login page.</p>
      </div>
    );
  }

  const lessonsOpenInShell = onLessonsRoute && lessonsPanelOpen && !focusChromeHidden;
  const sidebarExpanded = !focusChromeHidden && sidebarOpen;
  const showDevResetTools = isAdmin && devResetToolsEnabled && devAllowTutorDashboardAccess;
  const showTutorDashboardLink = canAccessTutorTools(studentId, currentRole);
  if (currentRole === "tutor") {
    return (
      <TutorPortalShell
        studentId={studentId}
        studentName={displayStudentName}
        roleName={displayRoleName}
        location={location}
        navigate={navigate}
      />
    );
  }
  const latestUsageSource = formatUsageSourceLabel(llmUsage?.latest_call_usage_source);
  const latestTokensDisplay =
    latestUsageSource === "confirmed"
      ? formatTokenCompact(llmUsage?.latest_call_tokens)
      : latestUsageSource === "estimated"
      ? `~${formatTokenCompact(llmUsage?.latest_call_estimated_tokens)}`
      : "--";
  const portalContext = {
    studentId,
    studentName: displayStudentName,
    authRole: currentRole,
    isAdmin,
    readOnly: isStudentView(),
    todayLabel,
    goToDashboardV2,
    goToLessons,
    goToFirstLesson,
    goToCurrentLesson: () => navigate(currentLessonPath),
    goToGuidedDemo,
    goToTutorNotes,
    goToAccount,
    closeSidebarPanel,
    lessonsPanelOpen,
    focusModeActive: focusChromeHidden,
    setFocusModeActive,
    isOnline,
    refreshLlmUsage,
    resetLlmUsage,
    currentWeek: shellWeek,
    currentLesson: shellLesson,
    dashboardReady: Boolean(dashboardData),
    currentLessonId: canonicalLessonId(dashboardData?.week, dashboardData?.lesson) || "W1L1",
    currentLessonPath:
      studentLessonPathFor(studentId, dashboardData?.week, dashboardData?.lesson) ||
      studentLessonPathFor(studentId, 1, 1),
  };

  return (
    <div
      className={`sp-shell ${focusChromeHidden ? "sp-shell--focus" : ""} ${
        lessonsOpenInShell ? "sp-shell--lessons-open" : "sp-shell--lessons-closed"
      } ${sidebarExpanded ? "sp-shell--sidebar-open" : "sp-shell--sidebar-closed"} ${
        onLesson ? "sp-shell--lesson-route" : ""
      } ${
        onGuidedDemo ? "sp-shell--guided-instructor" : ""
      } ${
        panelClosing ? "sp-shell--panel-closing" : ""
      }`.trim()}
    >
      {!focusChromeHidden && sidebarOpen ? (
        <div
          className="sp-sidebar-backdrop"
          aria-hidden="true"
          tabIndex={-1}
        />
      ) : null}

      <header className="sp-topbar" aria-hidden={focusChromeHidden}>
        <button
          type="button"
          className="sp-menu-toggle"
          aria-label={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
          aria-expanded={sidebarOpen ? "true" : "false"}
          onClick={() => setSidebarOpen((value) => !value)}
        >
          <SidebarToggleIcon open={sidebarOpen} />
        </button>
        <div className="sp-search" role="status" aria-label="Experience">
          <XPBar
            studentId={studentId}
            currentWeek={shellWeek}
            currentLesson={shellLesson}
          />
        </div>

        <div className="sp-user-actions">
          {!isOnline ? (
            <div className="sp-offline-indicator" role="status" aria-live="polite">
              <span className="sp-offline-dot" aria-hidden="true" />
              <span>Offline</span>
            </div>
          ) : null}
          <div className="sp-notifs" ref={notifWrapRef}>
            <button
              type="button"
              className="sp-iconbtn"
              aria-label={unreadCount ? `Notifications (${unreadCount} unread)` : "Notifications"}
              aria-haspopup="menu"
              aria-expanded={notifOpen ? "true" : "false"}
              onClick={() => setNotifOpen((v) => !v)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 22a2.5 2.5 0 0 0 2.45-2H9.55A2.5 2.5 0 0 0 12 22Z"
                  fill="currentColor"
                  opacity="0.95"
                />
                <path
                  d="M18 16V11.5c0-3.03-1.64-5.5-6-5.5s-6 2.47-6 5.5V16l-1.5 1.5V19h15v-1.5L18 16Z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
              {unreadCount ? (
                <span className="sp-badge" aria-hidden="true">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </button>

            {notifOpen ? (
              <div className="sp-menu" role="menu" aria-label="Notifications">
                <div className="sp-menu-title">Notifications</div>
                {notifLoading ? (
                  <div className="sp-menu-empty">Loading…</div>
                ) : notifications.length ? (
                  <div className="sp-menu-list">
                    {notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`sp-menu-item ${n.is_read ? "" : "unread"} ${isTutorGuidanceNotification(n.message) ? "is-clickable" : ""}`}
                        role="menuitem"
                        onClick={() => {
                          if (!isTutorGuidanceNotification(n.message)) return;
                          setNotifOpen(false);
                          goToTutorNotes();
                        }}
                      >
                        <div className="sp-menu-item-msg">{n.message}</div>
                        {(() => {
                          const lessonId = parseTutorGuidanceLessonId(n.message);
                          const matchedNote = lessonId ? tutorNoteLookup.get(lessonId) : null;
                          return matchedNote ? (
                            <div className="sp-menu-item-msg" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                              {matchedNote.note}
                            </div>
                          ) : null;
                        })()}
                        {isTutorGuidanceNotification(n.message) ? (
                          <button
                            type="button"
                            className="sp-cta ghost"
                            style={{ marginTop: 10, alignSelf: "flex-start" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setNotifOpen(false);
                              goToTutorNotes();
                            }}
                          >
                            Open Tutor Note
                          </button>
                        ) : null}
                        {n.created_at ? (
                          <div className="sp-menu-item-time">
                            {formatNotifTime(n.created_at)}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sp-menu-empty">No notifications yet.</div>
                )}
              </div>
            ) : null}
          </div>

          {showLlmUsagePanel ? (
            <div className="sp-llm-usage" ref={llmUsageWrapRef}>
              <button
                type="button"
                className="sp-llm-usage-btn"
                aria-haspopup="menu"
                aria-expanded={llmUsageOpen ? "true" : "false"}
                aria-label="LLM token usage"
                onClick={() => setLlmUsageOpen((v) => !v)}
              >
                LAST TOK: {llmUsageError ? "--" : latestTokensDisplay} | REQ:{" "}
                {llmUsageError ? "--" : Math.round(safeNumber(llmUsage?.latest_call_requests, 0))} | FN:{" "}
                {llmUsageError ? "--" : llmUsage?.last_function || "--"}
              </button>
              {llmUsageOpen ? (
                <div className="sp-llm-usage-menu" role="menu" aria-label="LLM usage details">
                  <div className="sp-llm-usage-row">
                    Latest call usage: {latestUsageSource}
                  </div>
                  <div className="sp-llm-usage-row">
                    Latest call note: {llmUsage?.latest_call_usage_note || "usage unavailable"}
                  </div>
                  <div className="sp-llm-usage-row">
                    Confirmed tokens: {Math.round(safeNumber(llmUsage?.latest_call_tokens, 0))}
                  </div>
                  <div className="sp-llm-usage-row">
                    Confirmed cost: {formatCost(llmUsage?.latest_call_cost)}
                  </div>
                  <div className="sp-llm-usage-row">
                    Estimated tokens: {Math.round(safeNumber(llmUsage?.latest_call_estimated_tokens, 0))}
                  </div>
                  <div className="sp-llm-usage-row">
                    Estimated cost: {formatCost(llmUsage?.latest_call_estimated_cost)}
                  </div>
                  <div className="sp-llm-usage-row">
                    Confirmed requests: {Math.round(safeNumber(llmUsage?.latest_call_requests, 0))}
                  </div>
                  <div className="sp-llm-usage-row">
                    Last function: {llmUsage?.last_function || "--"}
                  </div>
                  <div className="sp-llm-usage-row">
                    Session total tokens: {Math.round(safeNumber(llmUsage?.session_total_tokens, 0))}
                  </div>
                  <div className="sp-llm-usage-row">
                    Session total requests: {Math.round(safeNumber(llmUsage?.session_total_requests, 0))}
                  </div>
                  <div className="sp-llm-usage-row">
                    Session total cost: {formatCost(llmUsage?.session_total_cost)}
                  </div>
                  <div className="sp-llm-usage-row">Avg: {formatCost(llmUsage?.avg_cost)}</div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="sp-user">
            <div
              className="sp-avatar"
              aria-hidden
              style={(() => {
                if (!iconColor || iconColor === "default") return undefined;

                if (AVATAR_SOLID_COLOR_KEYS.has(iconColor)) {
                  return {
                    "--sp-avatar-solid": `var(--sp-avatar-${iconColor})`,
                    "--sp-avatar-hue": "0deg",
                  };
                }

                const hue = AVATAR_HUE_BY_KEY[iconColor];
                if (hue) {
                  return { "--sp-avatar-solid": undefined, "--sp-avatar-hue": hue };
                }

                return undefined;
              })()}
            >
              <span className="sp-avatar-letter">
                {avatarLabel}
              </span>
            </div>
            <div className="sp-user-meta">
              <div className="sp-name">{displayStudentName}</div>
              <div className="sp-user-sub">{displayRoleName}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="sp-body">
      {/* Sidebar */}
      <aside
        className="sp-side"
        aria-label="Student navigation"
        aria-hidden={focusChromeHidden || !sidebarOpen}
      >
        <div className="sp-side-top">
          <div className="sp-side-brand">
            <img
              src={process.env.PUBLIC_URL + "/hirilogo.png"}
              alt="HIRI logo"
              className="sp-side-logo"
            />
            <div className="sp-side-text">
              <div className="sp-side-title">Writing Studio</div>
              <div className="sp-side-sub">Student portal</div>
            </div>
          </div>

          <nav className="sp-side-nav" data-tour="dashboard-sidepanel">
            <button
              className={`sp-navitem ${onDashboardHome ? "active" : ""}`}
              type="button"
              onClick={() => {
                navigate(`/student/dashboard/${studentId}`);
                closeMobileDrawer();
              }}
            >
              Dashboard
            </button>
            <button
              className={`sp-navitem ${onLesson ? "active" : ""}`}
              type="button"
              onClick={goToLessons}
            >
              Lessons
            </button>
            {showStudentProgressLink ? (
              <button
                className={`sp-navitem ${onDashboardV2 ? "active" : ""}`}
                type="button"
                onClick={goToDashboardV2}
              >
                Progress
              </button>
            ) : null}
            {isAdmin ? (
              <button
                className="sp-navitem"
                type="button"
                onClick={() => {
                  navigate("/tutor/student-progress");
                  closeMobileDrawer();
                }}
              >
                Student Progress
              </button>
            ) : null}
            <button
              className={`sp-navitem ${onNotifications ? "active" : ""}`}
              type="button"
              onClick={goToNotifications}
            >
              Notifications
            </button>
            <button
              className={`sp-navitem ${onTutorNotes ? "active" : ""}`}
              type="button"
              onClick={goToTutorNotes}
            >
              Tutor Notes
            </button>
            <button
              className={`sp-navitem ${onBadges ? "active" : ""}`}
              type="button"
              onClick={goToBadges}
            >
              Badges
            </button>
              <button className="sp-navitem" type="button" onClick={() => navigate(`/student/${encodeURIComponent(studentId)}/course-complete`)}>
                Course Ending
              </button>
            {certificateReady ? (
              <div>
                <button className="sp-navitem" type="button" onClick={onDownloadCertificate} style={{ color: "#facc15" }}>
                  Download Certificate
                </button>
                {certificateDownloadError ? <p role="alert" className="sp-error">{certificateDownloadError}</p> : null}
              </div>
            ) : null}
            <button
              className={`sp-navitem ${onAccount ? "active" : ""}`}
              type="button"
              onClick={goToAccount}
            >
              Account
            </button>
            {showTutorDashboardLink ? (
              <button
                className="sp-navitem"
                type="button"
                onClick={() => {
                  navigate("/tutor/dashboard_v2", { state: { returnTo: currentRoute } });
                  closeMobileDrawer();
                }}
              >
                Tutor Dashboard
              </button>
            ) : null}
          </nav>
        </div>

        <div className="sp-side-bottom">
          <div className="sp-id-chip">ID: {studentId}</div>
          {showDevResetTools ? (
            <>
              <button
                className="sp-link sp-link-danger"
                type="button"
                onClick={handleResetProgress}
                disabled={resettingProgress}
              >
                {resettingProgress ? "Resetting..." : "Reset Progress"}
              </button>

              <DevStudentResetPanel
                defaultStudentId={studentId}
                defaultStudentName={displayStudentName}
                onNavigateLesson={goToLessonPosition}
                refreshSignal={llmUsage?.session_total_requests || 0}
              />
            </>
          ) : null}
          <button className="sp-link" type="button" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </aside>

      <section
        className={`sp-lessons-slot ${lessonsOpenInShell ? "is-open" : "is-closed"}`.trim()}
        aria-hidden={!lessonsOpenInShell}
      >
        {onLessonsRoute && !focusChromeHidden ? (
          <LessonsPane
            studentId={studentId}
            isOpen={lessonsPanelOpen}
            onClose={closeLessonsPanel}
            onLessonNavigate={closeLessonsPanel}
          />
        ) : null}
      </section>

      {/* Main */}
      <main className="sp-main">
        <div className="sp-main-scroll">
          {adminPreview ? (
            <div className="sp-admin-preview-banner" role="status">
              Admin preview: viewing Week {previewWeek || "Mentor"} Mentor page for {studentName || studentId} ({studentId})
            </div>
          ) : null}
          <Outlet
            context={portalContext}
          />

          <footer className="sp-footer" aria-hidden={focusChromeHidden}>
            © 2026 How I Remember It
          </footer>
        </div>
      </main>
      </div>
    </div>
  );
}

function TutorPortalShell({ studentId, studentName, roleName, location, navigate }) {
  const dashboardBase = "/tutor";
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const currentPath = location.pathname || "";
  const isCurriculumPath = currentPath.startsWith("/tutor/curriculum") || currentPath === dashboardBase;
  const isNotifications = currentPath.startsWith("/tutor/notifications");
  const isAccount = currentPath.startsWith("/tutor/account");
  const shellContext = {
    studentId,
    studentName,
    tutorMode: true,
    goToTutorNotes: () => navigate("/tutor/dashboard_v2"),
  };
  const accountContext = {
    studentId,
    studentName,
    tutorMode: true,
  };

  const closeMobileDrawer = () => {
    if (window.innerWidth <= 860) {
      setSidebarOpen(false);
    }
  };
  const goToCurriculum = () => {
    navigate("/tutor/curriculum");
    closeMobileDrawer();
  };
  const goToNotifications = () => {
    navigate("/tutor/notifications");
    closeMobileDrawer();
  };
  const goToAccount = () => {
    navigate("/tutor/account");
    closeMobileDrawer();
  };

  return (
    <div
      className={`sp-shell ${sidebarOpen ? "sp-shell--sidebar-open" : "sp-shell--sidebar-closed"} sp-shell--curriculum`}
    >
      <header className="sp-topbar" aria-hidden="false">
        <div className="sp-user-actions">
          <div className="sp-user">
            <div className="sp-avatar" aria-hidden>
              <span className="sp-avatar-letter">
                {String(studentName || studentId)
                  .replace(/\s+/g, "")
                  .slice(0, 2)
                  .toUpperCase() || "SM"}
              </span>
            </div>
            <div className="sp-user-meta">
              <div className="sp-name">{studentName || studentId}</div>
              <div className="sp-user-sub">{roleName}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="sp-body">
        <aside className="sp-side" aria-label="Tutor navigation">
          <div className="sp-side-top">
            <div className="sp-side-brand">
              <img
                src={process.env.PUBLIC_URL + "/hirilogo.png"}
                alt="HIRI logo"
                className="sp-side-logo"
              />
              <div className="sp-side-text">
                <div className="sp-side-title">Creative English Curriculum</div>
                <div className="sp-side-sub">Tutor Portal</div>
              </div>
            </div>

            <nav className="sp-side-nav" data-tour="dashboard-sidepanel">
              <button
                className={`sp-navitem ${isCurriculumPath ? "active" : ""}`}
                type="button"
                onClick={goToCurriculum}
              >
                Curriculum
              </button>
              <button
                className={`sp-navitem ${isNotifications ? "active" : ""}`}
                type="button"
                onClick={goToNotifications}
              >
                Notifications
              </button>
              <button
                className="sp-navitem"
                type="button"
                onClick={() => {
                  navigate("/tutor/dashboard_v2");
                  closeMobileDrawer();
                }}
              >
                Tutor Dashboard
              </button>
              <button
                className={`sp-navitem ${isAccount ? "active" : ""}`}
                type="button"
                onClick={goToAccount}
              >
                Account
              </button>
            </nav>
          </div>

          <div className="sp-side-bottom">
            <div className="sp-id-chip">ID: {studentId}</div>
            <button className="sp-link" type="button" onClick={() => navigate("/")}>
              Log out
            </button>
          </div>
        </aside>

        <main className="sp-main">
          <div className="sp-main-scroll">
            <div className="sp-content">
              {isNotifications ? (
                <StudentNotifications outletContext={shellContext} />
              ) : isAccount ? (
                <StudentAccount outletContext={accountContext} />
              ) : (
                <TutorCurriculumExplorer studentId={studentId} />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
